import clsx from 'clsx'

import { CASH_DESKS } from '@/config/station'
import {
  getCashDeskStatus,
  type CashDeskStatus,
  type Cashiers,
} from '@/services/api/schemas/station'
import { formatUpdatedAt, getErrorMessage } from '@/shared/lib/format'
import { Panel, Skeleton, StateBlock } from '@/shared/ui'

import s from './CashiersPanel.module.css'

interface CashiersPanelProps {
  /** Статус кассиров из /cashiers (ТЗ п. 4.3). */
  cashiers: Cashiers | undefined
  isLoading: boolean
  error: unknown
  onRetry: () => void
  className?: string
}

/** Подписи статусов по ТЗ п. 4.3; unavailable и no_data показываются одинаково. */
const STATUS_LABELS: Record<CashDeskStatus, string> = {
  present: 'На месте',
  absent: 'Отсутствует',
  unavailable: 'Статус недоступен',
  no_data: 'Статус недоступен',
}

export function CashiersPanel({
  cashiers,
  isLoading,
  error,
  onRetry,
  className,
}: CashiersPanelProps) {
  if (error) {
    return (
      <Panel title="Кассиры" className={className}>
        <StateBlock kind="error" message={getErrorMessage(error)} onRetry={onRetry} />
      </Panel>
    )
  }

  return (
    <Panel title="Кассиры" className={clsx(s.panel, className)} bodyClassName={s.body}>
      {CASH_DESKS.map((desk) => {
        const presence = cashiers?.cash_desks.find(
          (d) => d.cash_desk_number === desk.number,
        )?.presence
        const status = getCashDeskStatus(presence)
        const known = status === 'present' || status === 'absent'
        // У недоступной кассы своё updated_at — когда она отвечала в последний раз.
        // Показываем его, только если оно старше общего «Обновлено», иначе это дубль.
        const staleAt =
          !known && presence?.updated_at && presence.updated_at !== cashiers?.updated_at
            ? presence.updated_at
            : undefined
        return (
          <div key={desk.number} className={s.desk}>
            <span className={s.name}>{desk.name}</span>
            {isLoading ? (
              <Skeleton width={96} height={14} />
            ) : (
              <span className={s.state}>
                <span
                  className={clsx(
                    known ? s.cashier : s.unknown,
                    status === 'absent' && s.cashierOff,
                  )}
                >
                  {known && <i />}
                  {STATUS_LABELS[status]}
                </span>
                {staleAt && <span className={s.stale}>{formatUpdatedAt(staleAt)}</span>}
              </span>
            )}
          </div>
        )
      })}
      {/* Строка есть всегда, как у колонок; без данных — «Обновлено: —». */}
      <span className={s.updated}>
        {cashiers?.updated_at ? formatUpdatedAt(cashiers.updated_at) : 'Обновлено: —'}
      </span>
    </Panel>
  )
}
